import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "../../../utils/axiosConfig";
import { FaSearch } from "@react-icons/all-files/fa/FaSearch";
import "../../../Pages/Css/modalPermisosRoles.css"; // <- el CSS de arriba
import Swal from "sweetalert2";

const UPDATE_ENDPOINT = `${process.env.REACT_APP_SERVER}/usuarios/updatepermisos`;

const toBit = (v) =>
    Number(v === true || v === 1 || v === "1" || v === "true" || v === "TRUE");

const toBool = (v) =>
    v === true || v === 1 || v === "1" || v === "true" || v === "TRUE";

const getModulePresentation = (path = "") => {
    const value = path.toLowerCase();
    if (value.includes("trafico") || value.includes("dispositivo")) {
        return { icon: "bx bxs-truck", description: "Gestión y monitoreo de dispositivos y flotas" };
    }
    if (value.includes("usuario")) {
        return { icon: "bx bx-group", description: "Gestión de usuarios y accesos" };
    }
    if (value.includes("mapa") || value.includes("map")) {
        return { icon: "bx bx-map-alt", description: "Visualización y herramientas de mapa" };
    }
    if (value.includes("solicitud")) {
        return { icon: "bx bx-file", description: "Gestión y seguimiento de solicitudes" };
    }
    return { icon: "bx bx-grid-alt", description: "Configuración y permisos del módulo" };
};

const ModalPermissionsByRole = ({ data }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [q, setQ] = useState("");
    const [expandedRows, setExpandedRows] = useState({});

    // snapshot inmutable para detectar cambios
    const baseRowsRef = useRef([]);

    // Mapea a tu modelo, incluyendo IdPagina (clave para actualizar)
    const normalizeRow = (r, idx) => ({
        id: r.id ?? r.IdPagina ?? idx,
        IdPagina: r.IdPagina ?? r.id ?? idx,
        path: r.path ?? r.Pagina ?? r.DescripcionPagina ?? r.url ?? "",
        open: toBool(r.open ?? r.Abrir ?? r.bitOpen ?? false),
        create: toBool(r.create ?? r.Crear ?? r.bitInsert ?? false),
        edit: toBool(r.edit ?? r.Editar ?? r.bitEdit ?? false),
        remove: toBool(r.remove ?? r.Eliminar ?? r.bitDelete ?? false),
        note: r.Notas ?? r.note ?? "",
        _dirty: false,
    });

    const auth = {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("tkn")}`,
        },
        withCredentials: true,
    };

    const getInfoPermissionByRole = async (roleId) => {
        setLoading(true);
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_SERVER}/usuarios/getpermisosrol`,
                { tipouser: roleId },
                auth
            );
            if (res.data?.success === false) {
                window.location = "/";
                return;
            }
            const list = Array.isArray(res.data?.data) ? res.data.data : [];
            const norm = list.map(normalizeRow);
            setRows(norm);
            setExpandedRows({});
            baseRowsRef.current = norm.map((x) => ({ ...x, _dirty: false })); // snapshot
        } catch (e) {
            console.error("Error al obtener permisos:", e);
            setRows([]);
            baseRowsRef.current = [];
        } finally {
            setLoading(false);
        }
    };

    // Actualiza un permiso (y notas) para un rol.
    const updatePermiso = async (row, roleId, authCfg) => {
        const payload = {
            IdPagina: Number(row.IdPagina),
            IdRolP: Number(roleId),
            bitOpen: toBit(row.open),
            bitInsert: toBit(row.create),
            bitEdit: toBit(row.edit),
            bitDelete: toBit(row.remove),
            Notas: (row.note ?? "").trim(), // <- incluir notas
        };

        const { data } = await axios.post(UPDATE_ENDPOINT, payload, authCfg);
        if (data?.success === false) {
            window.location = "/";
            return false;
        }
        return true;
    };

    useEffect(() => {
        if (data?.IDRol) getInfoPermissionByRole(data.IDRol);
    }, [data?.IDRol]);

    // Filtrado
    const filtered = useMemo(() => {
        if (!q) return rows;
        const s = q.toLowerCase();
        return rows.filter((r) => r.path?.toLowerCase().includes(s));
    }, [q, rows]);

    // Helpers
    const markDirtyIfChanged = (a, b) => {
        const sameNote = (a.note ?? "").trim() === (b.note ?? "").trim();
        return (
            a.open !== b.open ||
            a.create !== b.create ||
            a.edit !== b.edit ||
            a.remove !== b.remove ||
            !sameNote
        );
    };

    const toggleCell = (id, key) =>
        setRows((prev) =>
            prev.map((r) => {
                if (r.id !== id) return r;
                const updated = { ...r, [key]: !r[key] };
                const base = baseRowsRef.current.find((x) => x.id === id) ?? r;
                return { ...updated, _dirty: markDirtyIfChanged(updated, base) };
            })
        );

    const toggleRowAll = (id, value) =>
        setRows((prev) =>
            prev.map((r) => {
                if (r.id !== id) return r;
                const updated = { ...r, open: value, create: value, edit: value, remove: value };
                const base = baseRowsRef.current.find((x) => x.id === id) ?? r;
                return { ...updated, _dirty: markDirtyIfChanged(updated, base) };
            })
        );

    const toggleColumnAll = (key, value) =>
        setRows((prev) =>
            prev.map((r) => {
                const updated = { ...r, [key]: value };
                const base = baseRowsRef.current.find((x) => x.id === r.id) ?? r;
                return { ...updated, _dirty: markDirtyIfChanged(updated, base) };
            })
        );

    const updateNote = (id, value) =>
        setRows((prev) =>
            prev.map((r) => {
                if (r.id !== id) return r;
                const updated = { ...r, note: value };
                const base = baseRowsRef.current.find((x) => x.id === id) ?? r;
                return { ...updated, _dirty: markDirtyIfChanged(updated, base) };
            })
        );

    const allChecked = (key) => filtered.length > 0 && filtered.every((r) => r[key]);
    const someChecked = (key) => filtered.some((r) => r[key]) && !allChecked(key);

    // Guardar SOLO cambios
    const onSave = async () => {
        if (!data?.IDRol) return;

        const diffs = rows.filter((r) => r._dirty);
        if (diffs.length === 0) {
            Swal.fire("Sin cambios", "No hay cambios para guardar.", "info");
            return;
        }

        setSaving(true);
        try {
            const results = await Promise.allSettled(
                diffs.map((r) =>
                    updatePermiso(r, data.IDRol, {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${localStorage.getItem("tkn")}`,
                        },
                        withCredentials: true,
                    })
                )
            );

            const okIds = results
                .map((res, i) => (res.status === "fulfilled" && res.value === true ? diffs[i].id : null))
                .filter(Boolean);

            const cleaned = rows.map((r) => (okIds.includes(r.id) ? { ...r, _dirty: false } : r));
            setRows(cleaned);
            baseRowsRef.current = cleaned.map((x) => ({ ...x }));

            Swal.fire("Permisos actualizados", "Los permisos se han actualizado correctamente.", "success");
        } catch (e) {
            console.error("Error al actualizar:", e?.response?.data || e.message);
            Swal.fire("Error al actualizar permisos", e?.response?.data || e.message, "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mpr p-2">
            <div className="mpr-mobile-title">
                <div className="mpr-mobile-title-icon"><i className="bx bx-shield-quarter" aria-hidden="true" /></div>
                <div>
                    <h2>Creación de permisos por rol</h2>
                    <p>Define los permisos de acceso por módulo y página para este rol.</p>
                </div>
            </div>
            {/* Header */}
            <div className="mpr-header d-flex flex-column flex-md-row align-items-stretch align-items-md-center mb-3">
                <h5 className="mb-2 mb-md-0">Permisos por página</h5>

                {/* derecha en desktop */}
                <div className="ms-md-auto d-none d-md-flex align-items-center gap-2">
                    <div className="input-group input-group-sm">
                        <span className="input-group-text">
                            <FaSearch />
                        </span>
                        <input
                            className="form-control"
                            placeholder="Buscar permisos..."
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            style={{ boxSizing: "border-box", height: 40 }}
                        />
                    </div>
                    <button
                        className="btn-primary-modals-permisos-roles mpr-save-desktop"
                        disabled={saving || loading}
                        onClick={onSave}
                        title="Guardar solo cambios"
                    >
                        {saving ? "Guardando..." : "Guardar"}
                    </button>
                </div>

                {/* buscador + botón en móvil */}
                <div className="d-md-none">
                    <div className="input-group input-group-sm mpr-search">
                        <span className="input-group-text">
                            <FaSearch />
                        </span>
                        <input
                            className="form-control"
                            placeholder="Buscar permisos..."
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            style={{ boxSizing: "border-box", height: 40 }}
                        />
                    </div>
                    <button
                        className="btn-primary-modals-permisos-roles mpr-save-mobile mt-2"
                        disabled={saving || loading}
                        onClick={onSave}
                        title="Guardar solo cambios"
                    >
                        {saving ? "Guardando..." : "Guardar"}
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-4">Cargando permisos…</div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-4 text-muted">Sin resultados.</div>
            ) : (
                <>
                    {/* Tabla ≥ md */}
                    <div className="table-responsive d-none d-md-block">
                        <table className="table table-sm table-hover align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th className="text-center" style={{ width: 60 }}>ID</th>
                                    <th style={{ minWidth: 420 }}>Carpeta / Página</th>
                                    <th className="text-center mpr-notes">Notas</th>
                                    {["open", "create", "edit", "remove"].map((key) => (
                                        <th key={key} className="text-center" style={{ width: 110 }}>
                                            <div className="form-check m-0 d-flex justify-content-center">
                                                <input
                                                    type="checkbox"
                                                    className={`form-check-input ${someChecked(key) ? "indeterminate" : ""}`}
                                                    checked={allChecked(key)}
                                                    onChange={(e) => toggleColumnAll(key, e.target.checked)}
                                                />
                                            </div>
                                            <div className="small text-muted text-center">
                                                {key === "open"
                                                    ? "Abrir"
                                                    : key === "create"
                                                        ? "Crear"
                                                        : key === "edit"
                                                            ? "Editar"
                                                            : "Eliminar"}
                                            </div>
                                        </th>
                                    ))}
                                    <th className="text-center" style={{ width: 110 }}>Todos</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((r) => {
                                    const rowAll = r.open && r.create && r.edit && r.remove;
                                    return (
                                        <tr key={r.id} className={r._dirty ? "table-warning" : ""}>
                                            <td className="text-center">{r.id}</td>
                                            <td className="text-break" title={r.path}>{r.path}</td>
                                            <td className="mpr-notes text-break">
                                                {r.note ? r.note : <span className="text-muted">-</span>}
                                            </td>
                                            <td className="text-center">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={r.open}
                                                    onChange={() => toggleCell(r.id, "open")}
                                                />
                                            </td>
                                            <td className="text-center">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={r.create}
                                                    onChange={() => toggleCell(r.id, "create")}
                                                />
                                            </td>
                                            <td className="text-center">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={r.edit}
                                                    onChange={() => toggleCell(r.id, "edit")}
                                                />
                                            </td>
                                            <td className="text-center">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={r.remove}
                                                    onChange={() => toggleCell(r.id, "remove")}
                                                />
                                            </td>
                                            <td className="text-center">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={rowAll}
                                                    onChange={(e) => toggleRowAll(r.id, e.target.checked)}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Tarjetas en < md */}
                    <div className="d-md-none">
                        <div className="d-flex flex-column gap-2">
                            {filtered.map((r) => {
                                const rowAll = r.open && r.create && r.edit && r.remove;
                                const presentation = getModulePresentation(r.path);
                                const isExpanded = expandedRows[r.id] ?? true;
                                return (
                                    <article key={r.id} className={`card p-2 mpr-card mpr-mobile-card ${r._dirty ? "border-warning" : ""} ${isExpanded ? "is-expanded" : ""}`}>
                                        <button
                                            type="button"
                                            className="mpr-mobile-card-heading"
                                            onClick={() => setExpandedRows((prev) => ({ ...prev, [r.id]: !isExpanded }))}
                                            aria-expanded={isExpanded}
                                        >
                                            <span className="mpr-mobile-card-icon"><i className={presentation.icon} aria-hidden="true" /></span>
                                            <span className="mpr-mobile-card-title">
                                                <strong>{r.path}</strong>
                                                <small>{presentation.description}</small>
                                            </span>
                                            <span className="mpr-mobile-card-chevron"><i className={`bx ${isExpanded ? "bx-chevron-up" : "bx-chevron-down"}`} aria-hidden="true" /></span>
                                        </button>

                                        {isExpanded && <div className="mpr-mobile-card-body">
                                            <label className="mpr-badged" htmlFor={`mpr-note-${r.id}`}>Notas</label>
                                            <textarea
                                                id={`mpr-note-${r.id}`}
                                                className="form-control mpr-note"
                                                placeholder="Escribe notas para esta página…"
                                                value={r.note ?? ""}
                                                maxLength={500}
                                                onChange={(e) => updateNote(r.id, e.target.value)}
                                                disabled
                                            />
                                            <span className="mpr-note-count">{(r.note ?? "").length}/500</span>

                                            <div className="mpr-permission-grid">
                                            {[
                                                ["open", "Abrir", "Puede acceder al módulo"],
                                                ["create", "Crear", "Puede crear registros"],
                                                ["edit", "Editar", "Puede modificar registros"],
                                                ["remove", "Eliminar", "Puede eliminar registros"],
                                            ].map(([key, label, description]) => (
                                                <label key={key} className="mpr-permission-option">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={r[key]}
                                                        onChange={() => toggleCell(r.id, key)}
                                                    />
                                                    <span className="mpr-permission-copy"><strong>{label}</strong><small>{description}</small></span>
                                                </label>
                                            ))}

                                            <label className="mpr-permission-option">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={rowAll}
                                                    onChange={(e) => toggleRowAll(r.id, e.target.checked)}
                                                />
                                                <span className="mpr-permission-copy"><strong>Todos</strong><small>Selecciona todos los permisos</small></span>
                                            </label>
                                            </div>
                                        </div>}
                                    </article>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ModalPermissionsByRole;
